import { useState, type ReactNode } from 'react';
import {
  Badge,
  Button,
  Combobox,
  DatePicker,
  Drawer,
  DrawerContent,
  DrawerTitle,
  NativeSelect,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  cn,
  type ComboboxOption,
} from '@eureca/ui';
import {
  DIVERSITY_FILTER_OPTIONS,
  hasAnyCandidateFilter,
  type CandidateFilterChip,
  type CandidateFiltersState,
  type CandidateLocationChip,
} from './candidate-filters';
import { PHASE_STATUS_LABEL, SUBSCRIPTION_STATUS_FILTER_OPTIONS } from './program-management-model';
import type { CandidateDiversityToken } from './program-candidates-query';
import { XIcon } from './icons';

/**
 * EU-593 — drawer "Filtrar candidatos" (frames Management / Filter). 11 campos
 * na ordem da RN-20, footer flutuante com Limpar/Aplicar (GPR-037..051).
 * Autocompletes sugerem 4 opções por vez (corte client-side); seleções
 * múltiplas acumulam como chips removíveis. NÃO existe filtro por fase — a
 * navegação por fase é exclusiva das abas (GPR-051).
 */
export interface CandidateFilterDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  values: CandidateFiltersState;
  onChange: (values: CandidateFiltersState) => void;
  onApply: () => void;
  onClear: () => void;
  /** Sugestões de localidade (cidades + estados) para o termo digitado. */
  locationOptions: CandidateLocationChip[];
  onLocationSearch: (term: string) => void;
  locationLoading?: boolean;
  courseTypeOptions: CandidateFilterChip[];
  courseAreaOptions: CandidateFilterChip[];
  courseGroupOptions: CandidateFilterChip[];
  onCourseGroupSearch: (term: string) => void;
  courseGroupLoading?: boolean;
  /** Há filtro APLICADO na listagem — habilita Aplicar mesmo com o form vazio
   *  (des-aplicar após Limpar filtro(s), GPR-050). */
  appliedFilterActive?: boolean;
}

const SUGGESTION_LIMIT = 4;

const normalize = (value: string) => value.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/**
 * "Até 4 sugestões por vez" (GPR-042/043/045): o corte de 4 vem DEPOIS do
 * termo digitado — cortar antes deixaria opções além das 4 primeiras
 * inalcançáveis (ex.: "Pessoas pretas" no campo de diversidade).
 */
function filterAndCap<T>(options: T[], term: string, labelOf: (opt: T) => string): T[] {
  const q = normalize(term.trim());
  const matched = q.length > 0 ? options.filter((o) => normalize(labelOf(o)).includes(q)) : options;
  return matched.slice(0, SUGGESTION_LIMIT);
}

export function CandidateFilterDrawer({
  open,
  onOpenChange,
  values,
  onChange,
  onApply,
  onClear,
  locationOptions,
  onLocationSearch,
  locationLoading,
  courseTypeOptions,
  courseAreaOptions,
  courseGroupOptions,
  onCourseGroupSearch,
  courseGroupLoading,
  appliedFilterActive = false,
}: CandidateFilterDrawerProps) {
  const anyFilter = hasAnyCandidateFilter(values);
  // GPR-050 — com filtro aplicado, Aplicar fica clicável mesmo com o form
  // vazio (aplicar o vazio = remover o recorte e apagar o indicador).
  const canApply = anyFilter || appliedFilterActive;
  const [diversityTerm, setDiversityTerm] = useState('');

  const locationKey = (l: CandidateLocationChip) =>
    l.kind === 'city' ? `city-${l.cityId}` : `state-${l.stateAcronym}`;

  const toggleChipList = (list: CandidateFilterChip[], chip: CandidateFilterChip) =>
    list.some((c) => c.id === chip.id) ? list.filter((c) => c.id !== chip.id) : [...list, chip];

  return (
    <Drawer open={open} onOpenChange={onOpenChange} direction="right" shouldScaleBackground={false}>
      <DrawerContent
        data-testid="program-candidates-filter-drawer"
        className="inset-x-auto inset-y-0 right-0 h-full w-full max-w-[605px] rounded-none border-l border-border [&>div:first-child]:hidden"
      >
        <div className="flex h-full flex-col">
          <div className="flex items-center justify-between px-8 pt-8 pb-4">
            <DrawerTitle className="text-2xl font-medium tracking-tight">
              Filtrar candidatos
            </DrawerTitle>
            <button
              type="button"
              aria-label="Fechar"
              data-testid="program-candidates-filter-close"
              onClick={() => onOpenChange(false)}
              className="rounded-md p-1 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            >
              <XIcon className="size-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-8 pb-6">
            <div className="flex flex-col gap-6">
              {/* 1 — Status da inscrições (select simples, GPR-039). */}
              <FilterField label="Status da inscrições">
                <NativeSelect
                  data-testid="candidates-filter-subscription-status"
                  value={values.subscriptionStatus ?? ''}
                  onChange={(e) =>
                    onChange({
                      ...values,
                      subscriptionStatus: (e.target.value ||
                        null) as CandidateFiltersState['subscriptionStatus'],
                    })
                  }
                >
                  <option value="">Selecione uma opção</option>
                  {SUBSCRIPTION_STATUS_FILTER_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </NativeSelect>
              </FilterField>

              {/* 2 — Status da fase (radio, GPR-040). */}
              <FilterField label="Status da fase">
                <div className="grid grid-cols-3 gap-3">
                  {(['not_started', 'in_progress', 'completed'] as const).map((status) => (
                    <RadioPill
                      key={status}
                      testId={`candidates-filter-phase-${status}`}
                      label={PHASE_STATUS_LABEL[status]}
                      checked={values.phaseStatus === status}
                      onClick={() =>
                        onChange({
                          ...values,
                          phaseStatus: values.phaseStatus === status ? null : status,
                        })
                      }
                    />
                  ))}
                </div>
              </FilterField>

              {/* 3 — Qualificado(a) (radio Sim/Não, GPR-040). */}
              <FilterField label="Qualificado(a)">
                <YesNoRadio
                  testIdPrefix="candidates-filter-qualified"
                  value={values.qualified}
                  onChange={(qualified) => onChange({ ...values, qualified })}
                />
              </FilterField>

              {/* 4 — Apto(a) (radio Sim/Não, GPR-040). */}
              <FilterField label="Apto(a)">
                <YesNoRadio
                  testIdPrefix="candidates-filter-apt"
                  value={values.apt}
                  onChange={(apt) => onChange({ ...values, apt })}
                />
              </FilterField>

              {/* 5 — Localidade (multi cidades/estados, GPR-041). */}
              <FilterField label="Localidade">
                <Combobox
                  data-testid="candidates-filter-location"
                  options={locationOptions
                    .slice(0, SUGGESTION_LIMIT)
                    .map((l): ComboboxOption => ({ value: locationKey(l), label: l.label }))}
                  onSelect={(key) => {
                    const chip = locationOptions.find((l) => locationKey(l) === key);
                    if (!chip) return;
                    const exists = values.locations.some((l) => locationKey(l) === key);
                    onChange({
                      ...values,
                      locations: exists
                        ? values.locations.filter((l) => locationKey(l) !== key)
                        : [...values.locations, chip],
                    });
                  }}
                  onSearchChange={onLocationSearch}
                  loading={locationLoading}
                  placeholder="Selecione uma ou mais cidades ou estados"
                  searchPlaceholder="Busque por cidade ou estado"
                  emptyMessage="Nenhuma localidade encontrada"
                />
                <ChipRow
                  chips={values.locations.map((l) => ({ key: locationKey(l), label: l.label }))}
                  onRemove={(key) =>
                    onChange({
                      ...values,
                      locations: values.locations.filter((l) => locationKey(l) !== key),
                    })
                  }
                />
              </FilterField>

              {/* 6 — Tipo de curso (multi, GPR-042). */}
              <MultiChipField
                label="Tipo de curso"
                testId="candidates-filter-course-type"
                options={courseTypeOptions}
                selected={values.courseTypes}
                onToggle={(chip) =>
                  onChange({ ...values, courseTypes: toggleChipList(values.courseTypes, chip) })
                }
                onRemove={(id) =>
                  onChange({
                    ...values,
                    courseTypes: values.courseTypes.filter((c) => c.id !== id),
                  })
                }
              />

              {/* 7 — Área de curso (multi, GPR-043). */}
              <MultiChipField
                label="Área de curso"
                testId="candidates-filter-course-area"
                options={courseAreaOptions}
                selected={values.courseAreas}
                onToggle={(chip) =>
                  onChange({ ...values, courseAreas: toggleChipList(values.courseAreas, chip) })
                }
                onRemove={(id) =>
                  onChange({
                    ...values,
                    courseAreas: values.courseAreas.filter((c) => c.id !== id),
                  })
                }
              />

              {/* 8 — Curso (multi, a partir dos grupos de curso do banco, GPR-044). */}
              <MultiChipField
                label="Curso"
                testId="candidates-filter-course-group"
                options={courseGroupOptions}
                selected={values.courseGroups}
                onToggle={(chip) =>
                  onChange({ ...values, courseGroups: toggleChipList(values.courseGroups, chip) })
                }
                onRemove={(id) =>
                  onChange({
                    ...values,
                    courseGroups: values.courseGroups.filter((c) => c.id !== id),
                  })
                }
                onSearch={onCourseGroupSearch}
                loading={courseGroupLoading}
              />

              {/* 9/10 — Datas de formação (date picker, GPR-047). */}
              <FilterField label="Data mínima de formação">
                <DatePicker
                  data-testid="candidates-filter-formation-min"
                  value={values.formationDateMin}
                  onChange={(formationDateMin) => onChange({ ...values, formationDateMin })}
                  placeholder="Selecione uma data"
                />
              </FilterField>
              <FilterField label="Data máxima de formação">
                <DatePicker
                  data-testid="candidates-filter-formation-max"
                  value={values.formationDateMax}
                  onChange={(formationDateMax) => onChange({ ...values, formationDateMax })}
                  placeholder="Selecione uma data"
                />
              </FilterField>

              {/* 11 — Critérios de diversidade (multi, opções exatas GPR-045). */}
              <FilterField label="Critérios de diversidade">
                <Combobox
                  data-testid="candidates-filter-diversity"
                  options={filterAndCap(
                    DIVERSITY_FILTER_OPTIONS.filter((opt) => !values.diversity.includes(opt.value)),
                    diversityTerm,
                    (opt) => opt.label
                  ).map((opt): ComboboxOption => ({ value: opt.value, label: opt.label }))}
                  onSearchChange={setDiversityTerm}
                  onSelect={(token) => {
                    const t = token as CandidateDiversityToken;
                    onChange({
                      ...values,
                      diversity: values.diversity.includes(t)
                        ? values.diversity.filter((d) => d !== t)
                        : [...values.diversity, t],
                    });
                  }}
                  placeholder="Selecione uma opção"
                  searchPlaceholder="Busque por critério"
                  emptyMessage="Nenhum critério encontrado"
                />
                <ChipRow
                  chips={values.diversity.map((token) => ({
                    key: token,
                    label: DIVERSITY_FILTER_OPTIONS.find((o) => o.value === token)?.label ?? token,
                  }))}
                  onRemove={(key) =>
                    onChange({
                      ...values,
                      diversity: values.diversity.filter((d) => d !== key),
                    })
                  }
                />
              </FilterField>
            </div>
          </div>

          {/* Footer flutuante (GPR-038/048/051). */}
          <div className="flex items-center justify-end gap-3 border-t border-border bg-background px-8 py-4">
            {anyFilter ? (
              <Button
                variant="ghost"
                data-testid="candidates-filter-clear"
                onClick={onClear}
                className="mr-auto"
              >
                Limpar filtro(s)
              </Button>
            ) : null}
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Voltar
            </Button>
            {canApply ? (
              <Button data-testid="candidates-filter-apply" onClick={onApply}>
                Aplicar filtro
              </Button>
            ) : (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span tabIndex={0} className="cursor-not-allowed">
                      <Button data-testid="candidates-filter-apply" disabled>
                        Aplicar filtro
                      </Button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>Selecione ao menos um filtro</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}

function FilterField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-2">
      <span className="text-sm font-medium text-foreground">{label}</span>
      {children}
    </div>
  );
}

function RadioPill({
  label,
  checked,
  onClick,
  testId,
}: {
  label: string;
  checked: boolean;
  onClick: () => void;
  testId: string;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={checked}
      data-testid={testId}
      onClick={onClick}
      className={cn(
        'flex items-center gap-2 rounded-lg border border-input bg-card px-4 py-3 text-left text-sm text-foreground',
        'hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
        checked && 'border-primary ring-1 ring-primary'
      )}
    >
      <span
        aria-hidden
        className={cn(
          'flex size-4 shrink-0 items-center justify-center rounded-full border border-input',
          checked && 'border-primary'
        )}
      >
        {checked ? <span className="size-2 rounded-full bg-primary" /> : null}
      </span>
      {label}
    </button>
  );
}

function YesNoRadio({
  value,
  onChange,
  testIdPrefix,
}: {
  value: boolean | null;
  onChange: (value: boolean | null) => void;
  testIdPrefix: string;
}) {
  return (
    <div className="grid grid-cols-2 gap-3">
      <RadioPill
        testId={`${testIdPrefix}-yes`}
        label="Sim"
        checked={value === true}
        onClick={() => onChange(value === true ? null : true)}
      />
      <RadioPill
        testId={`${testIdPrefix}-no`}
        label="Não"
        checked={value === false}
        onClick={() => onChange(value === false ? null : false)}
      />
    </div>
  );
}

function ChipRow({
  chips,
  onRemove,
}: {
  chips: { key: string; label: string }[];
  onRemove: (key: string) => void;
}) {
  if (chips.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-2 pt-1">
      {chips.map((chip) => (
        <Badge key={chip.key} className="gap-1 bg-secondary text-secondary-foreground">
          {chip.label}
          <button
            type="button"
            aria-label={`Remover ${chip.label}`}
            data-testid={`candidates-filter-chip-${chip.key}-remove`}
            onClick={() => onRemove(chip.key)}
            className="rounded-full p-0.5 hover:bg-primary/10"
          >
            <XIcon className="size-3" />
          </button>
        </Badge>
      ))}
    </div>
  );
}

function MultiChipField({
  label,
  testId,
  options,
  selected,
  onToggle,
  onRemove,
  onSearch,
  loading,
}: {
  label: string;
  testId: string;
  options: CandidateFilterChip[];
  selected: CandidateFilterChip[];
  onToggle: (chip: CandidateFilterChip) => void;
  onRemove: (id: number) => void;
  onSearch?: (term: string) => void;
  loading?: boolean;
}) {
  // Termo local: catálogos carregados de uma vez (tipo/área) filtram aqui;
  // busca server-side (curso) também repassa o termo via onSearch.
  const [term, setTerm] = useState('');
  return (
    <FilterField label={label}>
      <Combobox
        data-testid={testId}
        options={filterAndCap(
          options.filter((opt) => !selected.some((c) => c.id === opt.id)),
          term,
          (opt) => opt.label
        ).map((opt): ComboboxOption => ({ value: String(opt.id), label: opt.label }))}
        onSelect={(value) => {
          const chip = options.find((opt) => String(opt.id) === value);
          if (chip) onToggle({ id: chip.id, label: chip.label });
        }}
        onSearchChange={(t) => {
          setTerm(t);
          onSearch?.(t);
        }}
        loading={loading}
        placeholder="Selecione uma opção"
        searchPlaceholder="Busque por uma opção"
        emptyMessage="Nenhum resultado"
      />
      <ChipRow
        chips={selected.map((c) => ({ key: String(c.id), label: c.label }))}
        onRemove={(key) => onRemove(Number(key))}
      />
    </FilterField>
  );
}
